import { BoltIcon } from '@heroicons/react/24/solid';
import React from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  MedRecordsNextAction,
  MrFollowupNextAction,
  VoiceAiNotificationCategory,
} from '@/services/api/generated/treatmentRecordRequestsService';

interface MRNextActionPanelProps {
  category: VoiceAiNotificationCategory | null;
  nextAction: MedRecordsNextAction | MrFollowupNextAction;
  nextActionDescription: string;
  paymentAmount: string | null;
  // Context info to display in card
  subcategoryDescription?: string | null;
  timeline?: string | null;
  shouldTrust?: boolean | null;
  isActionRequired?: boolean;
  // Action handlers
  onMarkPaid?: () => void;
  // Loading states
  isMarkPaidLoading?: boolean;
}

const nextActionLabels: Record<MedRecordsNextAction, string> = {
  wait_for_timeline: 'Wait for Timeline',
  wait_for_records: 'Wait for Records',
  check_inbox: 'Check Inbox',
  resubmit_request: 'Resubmit Request',
  use_alternate_contact: 'Use Alternate Contact',
  pay_invoice: 'Pay Invoice',
  verify_patient_info: 'Verify Patient Info',
  provide_missing_docs: 'Provide Missing Docs',
  review_call_manually: 'Review Call Manually',
  escalate_to_human: 'Escalate to Human',
  wait_for_retry: 'Retry Scheduled',
  retry_during_business_hours: 'Retry During Business Hours',
  no_action_needed: 'No Action Needed',
};

const vnextActionLabels: Partial<Record<MrFollowupNextAction, string>> = {
  PAY_INVOICE: 'Pay Invoice',
  CHECK_INBOX_FOR_RECORDS: 'Check Inbox',
  ESCALATE_TO_BPO: 'Escalate to BPO',
  STOP_NO_ACTION: 'No Action Needed',
  STOP_REJECTED: 'Stopped',
  WAIT_FOR_RECORDS_DELIVERY: 'Wait for Records',
  WAIT_FOR_PROCESSING: 'Wait for Processing',
  WAIT_FOR_INVOICE: 'Wait for Invoice',
  WAIT_FOR_PAYMENT_CONFIRMATION: 'Wait for Payment Confirmation',
  WAIT_FOR_CALLBACK: 'Wait for Callback',
  RETRY_CALL: 'Retry Call',
};

function toTitleFromEnumToken(token: string) {
  return token
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function getActionLabel(action: MedRecordsNextAction | MrFollowupNextAction) {
  if (action in nextActionLabels) {
    return nextActionLabels[action as MedRecordsNextAction];
  }

  const vnextOverride = vnextActionLabels[action as MrFollowupNextAction];
  if (vnextOverride) return vnextOverride;

  return toTitleFromEnumToken(String(action));
}

function truncateRationale(text: string, limit = 140) {
  const trimmed = text.trim();
  if (!trimmed) return '';

  const match = trimmed.match(/[.!?](\s|$)/);
  const firstSentence = match
    ? trimmed.slice(0, (match.index ?? 0) + 1)
    : trimmed;

  if (firstSentence.length <= limit) return firstSentence;
  return `${firstSentence.slice(0, Math.max(0, limit - 3)).trim()}...`;
}

function isVnextAction(action: MedRecordsNextAction | MrFollowupNextAction) {
  return String(action).toUpperCase() === String(action);
}

export const MRNextActionPanel: React.FC<MRNextActionPanelProps> = ({
  category,
  nextAction,
  nextActionDescription,
  paymentAmount,
  subcategoryDescription,
  timeline,
  shouldTrust,
  isActionRequired,
  onMarkPaid,
  isMarkPaidLoading = false,
}) => {
  const actionLabel = getActionLabel(nextAction);
  const isActionNeeded = isActionRequired ?? category === 'action_needed';
  const isVnext = isVnextAction(nextAction);
  const showNeedsReview = isVnext && shouldTrust === false;
  const descriptionText = isVnext
    ? truncateRationale(nextActionDescription)
    : nextActionDescription;

  // Determine which action button to show
  // Only show functional buttons - Retry and Escalate are hidden until backend is ready
  const showPayButton =
    (nextAction === 'pay_invoice' || nextAction === 'PAY_INVOICE') &&
    onMarkPaid;

  // Get accent border color based on category
  const getBorderColor = () => {
    switch (category) {
      case 'progress_made':
        return 'border-l-green-500';
      case 'action_needed':
        return 'border-l-amber-500';
      case 'auto_retry':
        return 'border-l-blue-500';
      default:
        return 'border-l-gray-300';
    }
  };

  return (
    <div
      className={cn(
        'bg-gray-50 border border-gray-200 rounded-md p-3 border-l-[3px]',
        getBorderColor(),
      )}
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-900">
              {actionLabel}
            </span>
            {isActionNeeded && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded">
                <BoltIcon className="h-3 w-3" />
                ACTION REQUIRED
              </span>
            )}
            {showNeedsReview && (
              <span className="inline-flex items-center px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded">
                Needs review
              </span>
            )}
          </div>
          {descriptionText && (
            <p
              className={cn(
                'mt-1 text-sm',
                showNeedsReview ? 'text-gray-500' : 'text-gray-600',
              )}
            >
              {descriptionText}
            </p>
          )}
          {/* Context info: timeline and subcategory */}
          {(timeline || subcategoryDescription) && (
            <p className="mt-2 text-xs text-gray-500">
              {[subcategoryDescription, timeline].filter(Boolean).join(' · ')}
            </p>
          )}
          {paymentAmount && (
            <p className="mt-1 text-sm font-medium text-gray-800">
              Amount: {paymentAmount}
            </p>
          )}
        </div>
      </div>

      {/* Action buttons - only show when there are functional actions */}
      {showPayButton && (
        <div className="mt-3 flex items-center justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={onMarkPaid}
            disabled={isMarkPaidLoading}
          >
            {isMarkPaidLoading ? 'Marking...' : 'Mark Invoice Paid'}
          </Button>
        </div>
      )}
    </div>
  );
};
